/**
 * src/components/funnel/Testimonials.jsx
 *
 * The video wall and the written reviews, restyled for the new system.
 *
 * The behaviour is carried over from the gold-era components unchanged,
 * because it was right: a video card shows a lazy thumbnail and only mounts
 * the YouTube iframe once someone clicks it. Forty embedded players on page
 * load would have cost several megabytes and a fistful of third-party cookies
 * before anyone pressed play.
 *
 * What changed is the frame. These are now grids that take a `limit` and no
 * heading of their own, so the page decides how much proof to show and what to
 * call it, rather than each component asserting its own section.
 *
 * The photographic grade (`--grade-filter`, plus grain) is applied to every
 * thumbnail. One LUT over every image is the part of the visual system that
 * does the most work — without it, forty YouTube thumbnails at forty different
 * exposures read as a contact sheet from forty different shoots.
 */

import React, { useState } from "react";
import { Card, cx } from "./primitives";

/* --- video ---------------------------------------------------------------- */

function VideoCard({ title, youtubeId, delay }) {
  const [playing, setPlaying] = useState(false);


  return (
    <div
      data-testid={`video-card-${youtubeId}`}
      className="rp-reveal overflow-hidden rounded-[var(--radius-card)] border border-[color:var(--hairline-soft)] bg-ink-700 transition-colors duration-500 hover:border-[color:var(--hairline)]"
      data-reveal-delay={delay}
    >
      <div className="relative aspect-video bg-ink-600">
        {playing ? (
          <iframe
            className="absolute inset-0 h-full w-full"
            src={`https://www.youtube.com/embed/${youtubeId}?autoplay=1&rel=0&modestbranding=1&playsinline=1`}
            title={title}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        ) : (
          <button
            type="button"
            onClick={() => setPlaying(true)}
            className="group absolute inset-0 h-full w-full"
            aria-label={`Play testimonial: ${title}`}
            data-testid={`video-play-${youtubeId}`}
          >
            <img
              src="/images/growth-creative/client-story-cover-640.webp"
              width="640"
              height="360"
              alt=""
              loading="lazy"
              decoding="async"
              className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
              style={{ filter: "var(--grade-filter)" }}

            />
            {/* Bottom protection, so a baked-in title stays legible over a
                bright thumbnail without blacking out a dark one. */}
            <div
              aria-hidden="true"
              className="pointer-events-none absolute inset-0"
              style={{ background: "var(--protect-bottom)" }}
            />
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-brand text-white transition-transform duration-300 group-hover:scale-110 md:h-16 md:w-16">
                <svg viewBox="0 0 24 24" fill="currentColor" className="ml-0.5 h-6 w-6 md:h-7 md:w-7" aria-hidden="true">
                  <path d="M8 5v14l11-7z" />
                </svg>
              </span>
            </div>
            <div className="absolute inset-x-4 bottom-3 text-left">
              <span className="rp-eyebrow text-[10px] text-white/60">Client video</span>
              <div className="mt-0.5 line-clamp-2 text-sm font-semibold text-white md:text-[15px]">
                {title}
              </div>
            </div>
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * @param {object[]} videos  rows from /api/testimonials
 * @param {number}   limit   how many to show before the "load more" control
 */
export function VideoWall({ videos = [], limit = 6, className }) {
  const [shown, setShown] = useState(limit);

  if (videos.length === 0) return null;

  const visible = videos.slice(0, shown);
  const remaining = videos.length - shown;

  return (
    <div className={className} data-testid="video-testimonials">
      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {visible.map((v, i) => (
          <VideoCard
            key={v.youtube_id}
            title={v.title}
            youtubeId={v.youtube_id}
            delay={(i % 3) * 90}
          />
        ))}
      </div>

      {remaining > 0 && (
        <div className="mt-10 flex justify-center">
          <button
            type="button"
            onClick={() => setShown((s) => s + limit)}
            data-testid="load-more-videos"
            className="rounded-[var(--radius-btn)] border border-[color:var(--hairline-soft)] px-7 py-3 text-sm text-[color:var(--rp-body)] transition-colors hover:border-[color:var(--hairline)] hover:text-[color:var(--rp-heading)]"
          >
            Load {Math.min(limit, remaining)} more · {remaining} remaining
          </button>
        </div>
      )}
    </div>
  );
}

/* --- written -------------------------------------------------------------- */

const initials = (name) =>
  name
    .split(/\s+/)
    .map((w) => w[0])
    .filter(Boolean)
    .join("")
    .slice(0, 2)
    .toUpperCase();

function ReviewCard({ review, index, delay }) {
  return (
    <Card
      as="article"
      className="rp-reveal flex flex-col"
      data-testid={`review-${index}`}
      data-reveal-delay={delay}
    >
      <blockquote className="m-0 flex-1 text-[15px] leading-relaxed text-[color:var(--rp-body)]">
        “{review.review}”
      </blockquote>
      <figcaption className="mt-6 flex items-center gap-3 border-t border-[color:var(--hairline-soft)] pt-5">
        <span
          aria-hidden="true"
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[color:var(--hairline)] text-sm font-semibold text-brand-400"
        >
          {initials(review.name)}
        </span>
        <span>
          <span className="block text-sm font-semibold text-[color:var(--rp-heading)]">
            {review.name}
          </span>
          {review.verified && <span className="rp-eyebrow text-[10px]">Verified review</span>}
        </span>
      </figcaption>
    </Card>
  );
}

export function ReviewGrid({ written = [], limit = 6, className }) {
  const [shown, setShown] = useState(limit);

  if (written.length === 0) return null;

  const visible = written.slice(0, shown);
  const remaining = written.length - shown;

  return (
    <div className={className} data-testid="written-testimonials">
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {visible.map((t, i) => (
          <ReviewCard key={`${t.name}-${i}`} review={t} index={i} delay={(i % 3) * 90} />
        ))}
      </div>

      {remaining > 0 && (
        <div className="mt-10 flex justify-center">
          <button
            type="button"
            onClick={() => setShown((s) => s + limit)}
            data-testid="load-more-reviews"
            className="rounded-[var(--radius-btn)] border border-[color:var(--hairline-soft)] px-7 py-3 text-sm text-[color:var(--rp-body)] transition-colors hover:border-[color:var(--hairline)] hover:text-[color:var(--rp-heading)]"
          >
            Load {Math.min(limit, remaining)} more · {remaining} remaining
          </button>
        </div>
      )}
    </div>
  );
}

/* --- the one huge quote --------------------------------------------------- */

/**
 * QuoteBillboard — one testimonial at display scale.
 *
 * Deliberately picks nothing itself: the caller passes the quote it wants
 * featured, so a page cannot end up billboarding whichever review happens to
 * sort first today.
 */
export function QuoteBillboard({ quote, name, className }) {
  if (!quote) return null;
  return (
    <figure className={cx("rp-reveal m-0 max-w-4xl", className)}>
      <blockquote className="rp-h3 m-0 font-semibold text-[color:var(--rp-heading)]">
        “{quote}”
      </blockquote>
      <figcaption className="rp-eyebrow mt-8">{name}</figcaption>
    </figure>
  );
}
